import React, { useEffect, useState } from "react";
import { api } from "../../../../lib/api";

/**
 * Card Layout A/B Experiment Panel
 *
 * Side-by-side scan-rate + time-to-first-scan comparison between the two
 * card-image layouts that /api/cards/issue randomly assigns 50/50:
 *   • v1-small-qr — legacy: 96pt PAN + 200px corner QR
 *   • v2-big-qr   — new:    60pt PAN + 540px centered QR
 *
 * Helps the admin decide whether to lock in the big-QR redesign as the
 * default once a statistically meaningful sample of receivers has scanned.
 */
function fmtMinutes(m) {
  if (m === null || m === undefined) return "—";
  if (m < 1) return "<1 min";
  if (m < 60) return `${Math.round(m)} min`;
  if (m < 60 * 24) return `${(m / 60).toFixed(1)} h`;
  return `${(m / (60 * 24)).toFixed(1)} d`;
}

function Cohort({ data, label, accent }) {
  return (
    <div className="flex-1 min-w-[240px] border border-[#E5E0D8] rounded-xl p-4 bg-[#FBF9F6]" data-testid={`layout-cohort-${data.version}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{label}</p>
        <span className={`mono text-[10px] px-2 py-0.5 rounded-full ${accent}`}>{data.version}</span>
      </div>
      <p className="serif text-3xl mt-2 mono" data-testid={`cohort-scan-rate-${data.version}`}>
        {data.scan_rate_pct}%
        <span className="text-sm text-[#4A5C50] ml-2">scan rate</span>
      </p>
      <p className="text-xs text-[#4A5C50] mt-1 mono">
        {data.cards_with_any_scan} of {data.total_cards} cards scanned ≥1×
      </p>
      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Total scans</p>
          <p className="mono font-semibold text-[#184A30]" data-testid={`cohort-total-scans-${data.version}`}>
            {data.total_scans}
          </p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Avg / card</p>
          <p className="mono font-semibold text-[#184A30]">{data.avg_scans_per_card}</p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Avg time→1st scan</p>
          <p className="mono font-semibold text-[#184A30]" data-testid={`cohort-avg-ttfs-${data.version}`}>
            {fmtMinutes(data.avg_time_to_first_scan_minutes)}
          </p>
        </div>
        <div>
          <p className="text-[9px] tracking-widest uppercase text-[#4A5C50]">Median</p>
          <p className="mono font-semibold text-[#184A30]">{fmtMinutes(data.median_time_to_first_scan_minutes)}</p>
        </div>
      </div>
    </div>
  );
}

export function CardLayoutExperimentPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/admin/card-layout-experiment")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Could not load experiment data"));
  }, []);

  if (error) return null; // Tile silently hidden when the endpoint isn't ready.
  if (!data) return null;
  const [v1, v2] = data.cohorts;

  return (
    <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6" data-testid="card-layout-experiment-panel">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">
            Card Layout A/B Experiment · random 50/50
          </p>
          <p className="serif text-2xl mt-1">
            Big-QR vs small-QR scan rates
          </p>
          <p className="text-xs text-[#4A5C50] mono mt-1">{data.note}</p>
        </div>
        <div className="text-right">
          {data.can_compare ? (
            <div data-testid="experiment-verdict">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Lift (v2 vs v1)</p>
              <p className={`serif text-2xl mt-1 mono ${data.scan_rate_lift_pct > 0 ? "text-[#184A30]" : "text-[#B04E2F]"}`}>
                {data.scan_rate_lift_pct !== null
                  ? `${data.scan_rate_lift_pct > 0 ? "+" : ""}${data.scan_rate_lift_pct}%`
                  : "—"}
              </p>
              {data.winner && (
                <p className="text-xs mono mt-1 text-[#4A5C50]">
                  Winner: <span className="font-semibold text-[#184A30]">{data.winner}</span>
                </p>
              )}
            </div>
          ) : (
            <div className="text-right" data-testid="experiment-needs-data">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Sample size</p>
              <p className="text-sm text-[#4A5C50] mono mt-1">
                Need ≥{data.min_sample_size} cards in each cohort.<br />
                Currently: v1={v1.total_cards} · v2={v2.total_cards}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-4">
        <Cohort data={v1} label="Legacy layout" accent="bg-[#E5E0D8] text-[#4A5C50]" />
        <Cohort data={v2} label="New layout" accent="bg-[#D96C4A]/20 text-[#B04E2F]" />
      </div>
    </div>
  );
}
